import { Button, Nav, NavDropdown, Navbar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import { ApplicationPaths, LOGGER_LOG_TYPE } from 'Config';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import LanguagesService from 'api/languages/LanguagesService';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import AccountService from 'api/account/AccountService';
import { setProfile } from 'store/authentication/action';
import Logger from 'common/services/Logger';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import drawLocales, { Language } from 'leaflet-draw-locales';

interface NavBarProps {
    onToggleSidebar?: () => void;
}

const getInitials = (name?: string | null): string => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const NavBar = ({ onToggleSidebar }: NavBarProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const [langs, setLangs] = useState<any[]>([]);
    const [lang, setLang] = useState<any>([]);

    const changeLang = (lang: SelectValueLabel) => {
        AccountService.changeLang(lang)
            .finally(() => {
                getProfile().catch(console.error);
                setLang(lang);
                drawLocales(lang.label as Language);
            });
    }

    useEffect(() => {
        LanguagesService.GetAllForSelectItem()
            .then((data) => {
                setLangs(data);
                setLang(data.filter(l => l.value === loggedUser?.languageId)[0]);
                drawLocales(lang.label as Language);
            })
            .catch(() => Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get languages'));
    }, []);

    const getProfile = async () => {
        try {
            const profile = await AccountService.profile();
            dispatch(setProfile(profile) as any);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get user profile', error);
        }
    }

    return (
        <Navbar className="main-header h-navbar-height px-3 bg-white border-bottom" style={{ gridArea: 'header', borderColor: '#F1F5F9' }}>
            <Button variant="link" className="d-sm-none text-slate-500 p-0 border-0" onClick={onToggleSidebar}>
                <FontAwesomeIcon icon={faBars} className="text-lg" />
            </Button>

            <Navbar.Collapse className="justify-content-end">
                <Nav className="align-items-center">
                    {lang && (
                        <NavDropdown
                            title={<><FontAwesomeIcon icon={faGlobe} className="me-1 opacity-50" />{lang?.fullLabel}</>}
                            align="end"
                        >
                            {langs.map(l => (
                                <NavDropdown.Item onClick={() => changeLang(l)} key={l.value}>{l.fullLabel}</NavDropdown.Item>
                            ))}
                        </NavDropdown>
                    )}
                    <NavDropdown
                        title={
                            <span className="d-inline-flex align-items-center gap-2" style={{ maxWidth: '10rem' }}>
                                <span
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle text-white flex-shrink-0"
                                    style={{
                                        width: 28,
                                        height: 28,
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        background: 'linear-gradient(135deg, #38bdf8, #6366f1)',
                                    }}
                                >
                                    {getInitials(loggedUser?.realName)}
                                </span>
                                <span className="text-truncate">{loggedUser?.realName}</span>
                            </span>
                        }
                        align="end"
                    >
                        <NavDropdown.Item onClick={() => navigate('/account/details')}>{t('common.profile')}</NavDropdown.Item>
                        <NavDropdown.Divider />
                        <NavDropdown.Item onClick={() => navigate(`${ApplicationPaths.LogOut}`)}>{t('common.logout')}</NavDropdown.Item>
                    </NavDropdown>
                </Nav>
            </Navbar.Collapse>
        </Navbar>
    );
};

export default NavBar;
